import { useEffect, useRef, useState } from "react";
import {
  CONSENT_OPEN_EVENT,
  guardarConsentimiento,
  leerConsentimiento,
  aplicarConsentimiento,
} from "@/lib/consent";

export function PrivacyPreferences() {
  const [visible, setVisible] = useState(false);
  const [config, setConfig] = useState(false);
  const [analitica, setAnalitica] = useState(false);
  const [externo, setExterno] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const guardado = leerConsentimiento();
    aplicarConsentimiento(guardado);
    if (!guardado) setVisible(true);

    const abrir = () => {
      const actual = leerConsentimiento();
      setAnalitica(actual?.analitica ?? false);
      setExterno(actual?.externo ?? false);
      setConfig(true);
      setVisible(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, abrir);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, abrir);
  }, []);

  useEffect(() => {
    if (visible) panelRef.current?.focus();
  }, [visible]);

  if (!visible) return null;

  const guardar = (valor: { analitica: boolean; externo: boolean }) => {
    guardarConsentimiento(valor);
    setVisible(false);
    setConfig(false);
  };

  const boton =
    "min-h-11 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary";

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6">
      <div
        ref={panelRef}
        tabIndex={-1}
        role="region"
        aria-label="Preferencias de privacidad"
        className="pv-privacy-panel mx-auto max-w-[900px] rounded-2xl p-5 sm:p-6"
      >
        <h2 className="font-display text-lg font-bold text-navy">Preferencias de privacidad</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Usamos almacenamiento necesario para recordar esta elección. La analítica (Google
          Analytics) y el mapa de Google sólo se cargan si tú lo autorizas. Puedes cambiar o retirar
          tu elección cuando quieras desde “Preferencias de privacidad” en el pie de página.
        </p>

        {config && (
          <div className="mt-4 grid gap-3 rounded-xl bg-mist p-4">
            <p className="text-sm text-navy">
              <strong>Necesario:</strong> guarda tu elección de privacidad. Siempre activo.
            </p>
            <label className="flex items-start gap-3 text-sm text-navy">
              <input
                type="checkbox"
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                checked={analitica}
                onChange={(e) => setAnalitica(e.target.checked)}
              />
              <span>
                <strong>Analítica (Google Analytics 4):</strong> nos ayuda a entender cómo se usa el
                sitio.
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-navy">
              <input
                type="checkbox"
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                checked={externo}
                onChange={(e) => setExterno(e.target.checked)}
              />
              <span>
                <strong>Contenido externo (Google Maps):</strong> permite mostrar el mapa incrustado
                en el sitio.
              </span>
            </label>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            className={`${boton} border-primary bg-primary text-primary-foreground hover:bg-navy`}
            onClick={() => guardar({ analitica: true, externo: true })}
          >
            Aceptar analítica y mapa
          </button>
          <button
            type="button"
            className={`${boton} border-primary bg-background text-primary hover:bg-mist`}
            onClick={() => guardar({ analitica: false, externo: false })}
          >
            Rechazar no esenciales
          </button>
          {config ? (
            <button
              type="button"
              className={`${boton} border-primary bg-background text-primary hover:bg-mist`}
              onClick={() => guardar({ analitica, externo })}
            >
              Guardar elección
            </button>
          ) : (
            <button
              type="button"
              className={`${boton} border-primary bg-background text-primary hover:bg-mist`}
              onClick={() => {
                const actual = leerConsentimiento();
                setAnalitica(actual?.analitica ?? false);
                setExterno(actual?.externo ?? false);
                setConfig(true);
              }}
            >
              Configurar
            </button>
          )}
        </div>

        <p className="mt-3 text-sm text-muted-foreground">
          Más información en la{" "}
          <a className="font-medium text-primary underline" href="/cookies">
            Política de cookies
          </a>{" "}
          y la{" "}
          <a className="font-medium text-primary underline" href="/privacidad">
            Política de privacidad
          </a>
          .
        </p>
      </div>
    </div>
  );
}
